import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";

const defaultShaderSource = `#version 300 es
precision highp float;
out vec4 O;
uniform vec2 resolution;
uniform float time;
#define FC gl_FragCoord.xy
#define T time
#define R resolution
#define MN min(R.x,R.y)
float rnd(vec2 p) {
  p=fract(p*vec2(12.9898,78.233));
  p+=dot(p,p+34.56);
  return fract(p.x*p.y);
}
float noise(in vec2 p) {
  vec2 i=floor(p), f=fract(p), u=f*f*(3.-2.*f);
  float
  a=rnd(i),
  b=rnd(i+vec2(1,0)),
  c=rnd(i+vec2(0,1)),
  d=rnd(i+1.);
  return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
}
float fbm(vec2 p) {
  float t=.0, a=1.; mat2 m=mat2(1.,-.5,.2,1.2);
  for (int i=0; i<5; i++) {
    t+=a*noise(p);
    p*=2.*m;
    a*=.5;
  }
  return t;
}
float clouds(vec2 p) {
  float d=1., t=.0;
  for (float i=.0; i<3.; i++) {
    float a=d*fbm(i*10.+p.x*.2+.2*(1.+i)*p.y+d+i*i+p);
    t=mix(t,d,a);
    d=a;
    p*=2./(i+1.);
  }
  return t;
}
void main(void) {
  vec2 uv=(FC-.5*R)/MN,st=uv*vec2(2,1);
  vec3 col=vec3(0);
  float bg=clouds(vec2(st.x+T*.5,-st.y));
  uv*=1.-.3*(sin(T*.2)*.5+.5);
  for (float i=1.; i<12.; i++) {
    uv+=.1*cos(i*vec2(.1+.01*i, .8)+i*i+T*.5+.1*uv.x);
    vec2 p=uv;
    float d=length(p);
    col+=.00125/d*(cos(sin(i)*vec3(1,2,3))+1.);
    float b=noise(i+p+bg*1.731);
    col+=.002*b/length(max(p,vec2(b*p.x*.02,p.y)));
    col=mix(col,vec3(bg*.25,bg*.137,bg*.05),d);
  }
  O=vec4(col,1);
}`;

function useShaderBackground() {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef();
  const rendererRef = useRef(null);
  const pointersRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;

    // Check WebGL2 support
    const gl = canvas.getContext("webgl2");
    if (!gl) return;

    const dpr = Math.max(1, 0.5 * window.devicePixelRatio);

    // Simple WebGL renderer
    class Renderer {
      constructor() {
        this.gl = gl;
        this.scale = dpr;
        this.program = null;
        this.vs = null;
        this.fs = null;
        this.buffer = null;
        this.mouseMove = [0, 0];
        this.mouseCoords = [0, 0];
        this.pointerCoords = [0, 0];
        this.nbrOfPointers = 0;
        this.vertexSrc = `#version 300 es\nprecision highp float;\nin vec4 position;\nvoid main(){gl_Position=position;}`;
        this.vertices = [-1, 1, -1, -1, 1, 1, 1, -1];
      }

      compile(shader, source) {
        this.gl.shaderSource(shader, source);
        this.gl.compileShader(shader);
      }

      setup() {
        const g = this.gl;
        this.vs = g.createShader(g.VERTEX_SHADER);
        this.fs = g.createShader(g.FRAGMENT_SHADER);
        this.compile(this.vs, this.vertexSrc);
        this.compile(this.fs, defaultShaderSource);
        this.program = g.createProgram();
        g.attachShader(this.program, this.vs);
        g.attachShader(this.program, this.fs);
        g.linkProgram(this.program);
      }

      init() {
        const g = this.gl;
        const p = this.program;
        this.buffer = g.createBuffer();
        g.bindBuffer(g.ARRAY_BUFFER, this.buffer);
        g.bufferData(g.ARRAY_BUFFER, new Float32Array(this.vertices), g.STATIC_DRAW);
        const pos = g.getAttribLocation(p, "position");
        g.enableVertexAttribArray(pos);
        g.vertexAttribPointer(pos, 2, g.FLOAT, false, 0, 0);
        p._resolution = g.getUniformLocation(p, "resolution");
        p._time = g.getUniformLocation(p, "time");
        p._move = g.getUniformLocation(p, "move");
        p._touch = g.getUniformLocation(p, "touch");
        p._pointerCount = g.getUniformLocation(p, "pointerCount");
        p._pointers = g.getUniformLocation(p, "pointers");
      }

      render(now = 0) {
        const g = this.gl;
        const p = this.program;
        if (!p) return;
        g.clearColor(0, 0, 0, 1);
        g.clear(g.COLOR_BUFFER_BIT);
        g.useProgram(p);
        g.bindBuffer(g.ARRAY_BUFFER, this.buffer);
        g.uniform2f(p._resolution, canvas.width, canvas.height);
        g.uniform1f(p._time, now * 1e-3);
        g.uniform2f(p._move, ...this.mouseMove);
        g.uniform2f(p._touch, ...this.mouseCoords);
        g.uniform1i(p._pointerCount, this.nbrOfPointers);
        g.uniform2fv(p._pointers, this.pointerCoords);
        g.drawArrays(g.TRIANGLE_STRIP, 0, 4);
      }

      reset() {
        const g = this.gl;
        if (this.program) {
          if (this.vs) { g.detachShader(this.program, this.vs); g.deleteShader(this.vs); }
          if (this.fs) { g.detachShader(this.program, this.fs); g.deleteShader(this.fs); }
          g.deleteProgram(this.program);
        }
      }
    }

    // Pointer handler
    let active = false;
    const pointers = new Map();
    let lastCoords = [0, 0];
    let moves = [0, 0];

    const map = (x, y) => [x * dpr, canvas.height - y * dpr];

    canvas.addEventListener("pointerdown", (e) => {
      active = true;
      pointers.set(e.pointerId, map(e.clientX, e.clientY));
    });
    canvas.addEventListener("pointerup", (e) => {
      if (pointers.size === 1) lastCoords = pointers.values().next().value || lastCoords;
      pointers.delete(e.pointerId);
      active = pointers.size > 0;
    });
    canvas.addEventListener("pointerleave", (e) => {
      if (pointers.size === 1) lastCoords = pointers.values().next().value || lastCoords;
      pointers.delete(e.pointerId);
      active = pointers.size > 0;
    });
    canvas.addEventListener("pointermove", (e) => {
      if (!active) return;
      lastCoords = [e.clientX, e.clientY];
      pointers.set(e.pointerId, map(e.clientX, e.clientY));
      moves = [moves[0] + e.movementX, moves[1] + e.movementY];
    });

    const renderer = new Renderer();
    rendererRef.current = renderer;

    const resize = () => {
      const d = Math.max(1, 0.5 * window.devicePixelRatio);
      canvas.width = window.innerWidth * d;
      canvas.height = window.innerHeight * d;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    renderer.setup();
    renderer.init();
    resize();

    const loop = (now) => {
      const first = pointers.size > 0 ? pointers.values().next().value : lastCoords;
      const coords = pointers.size > 0 ? Array.from(pointers.values()).flat() : [0, 0];
      renderer.mouseCoords = first;
      renderer.nbrOfPointers = pointers.size;
      renderer.pointerCoords = coords;
      renderer.mouseMove = moves;
      renderer.render(now);
      animationFrameRef.current = requestAnimationFrame(loop);
    };
    loop(0);

    window.addEventListener("resize", resize);
    return () => {
      window.removeEventListener("resize", resize);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      renderer.reset();
    };
  }, []);

  return canvasRef;
}

export default function HeroSection() {
  const canvasRef = useShaderBackground();

  const scrollToAbout = () => {
    const el = document.querySelector("#about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="hero-section" id="hero" className="relative w-full h-screen overflow-hidden bg-black">
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full object-contain touch-none"
        style={{ background: "black" }}
      />

      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 z-[1]" />

      {/* Content */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-white px-4">
        {/* Trust badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-8"
        >
          <div className="flex items-center gap-2 px-6 py-3 bg-[#F59E0B]/10 backdrop-blur-md border border-[#F59E0B]/30 text-sm tracking-wider uppercase">
            <span className="text-[#F59E0B]">Discipline is the Aesthetic</span>
          </div>
        </motion.div>

        <div className="text-center space-y-6 max-w-5xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-['Bebas_Neue'] text-5xl md:text-7xl lg:text-8xl tracking-tight bg-gradient-to-r from-[#F59E0B] via-yellow-400 to-amber-300 bg-clip-text text-transparent"
          >
            Train like it matters.
          </motion.h1>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-['Bebas_Neue'] text-5xl md:text-7xl lg:text-8xl tracking-tight bg-gradient-to-r from-yellow-300 via-[#F59E0B] to-red-500 bg-clip-text text-transparent"
          >
            Live like it shows.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="max-w-3xl mx-auto text-base md:text-lg text-[#A3A3A3] font-light leading-relaxed"
          >
            A next-generation fitness space where consistency becomes identity. Dark minimal interiors, high-energy atmosphere, and a community built to push limits.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mt-10"
          >
            <Link
              to="/gym-tour"
              className="px-8 py-4 bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:scale-105 flex items-center justify-center text-center"
            >
              Take Virtual Tour
            </Link>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => {
                const el = document.querySelector("#contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-8 py-4 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 border border-[#F59E0B]/30 hover:border-[#F59E0B]/60 text-[#F59E0B] font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:scale-105 backdrop-blur-sm"
            >
              Get in Touch
            </button>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.button
          data-testid="scroll-indicator"
          onClick={scrollToAbout}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A3A3A3] hover:text-[#F59E0B] transition-colors cursor-pointer"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown size={28} />
          </motion.div>
        </motion.button>
      </div>
    </section>
  );
}
